<template>
  <div class="input-phone__wrapper" :class="{ 'input-phone__wrapper--disabled': disabled }">
    <span v-if="showLabel" class="input-phone__label caption color--dark-purple">
      {{ $t('adminTitles.phone') }}
    </span>
    <div class="input-phone color--black-purple" :class="{ 'input-phone--error': errorValidation }">
      <div class="input-phone__code" ref="codeElement" @click="toggleCodeSelector">
        <span class="body-text color--black-purple">{{
          selectedCountry ? selectedCountry.phoneCode : ''
          }}</span>
        <img class="input-phone__code-arrow" src="/img/renova-dashboard/chevron-left.svg" alt="chevron-left">
      </div>
      <input id="id" v-model="inputValue" ref="input" class="input-phone__form body-text color--black-purple"
        :placeholder="$t('adminTitles.phone')" :disabled="disabled"
        v-mask="selectedCountry ? selectedCountry.phoneMobileMask : defaultPhoneMask" @change="valueChange"
        @blur="validate(); $emit('onBlue')" @paste="handlePaste($event)" required>
      <div v-if="errorValidation" class="el-form-item__error">
        {{$t('adminTitles.EnterValidPhoneNumber')}}
      </div>

      <div v-if="showCodeSelector" class="input-phone__code-selector">
        <div class="input-phone__code-search" @click.stop>
          <input ref="codeSearch" v-model="codeSearch"
            class="input-phone__code-search-input body-text color--black-purple"
            :placeholder="$t('adminTitles.search')">
        </div>

        <div class="input-phone__code-list">
          <div v-for="item in filteredCodes" :key="item.id"
            class="input-phone__code-item body-text color--black-purple"
            :class="{ 'input-phone__code-item--active': selectedCountry && (item.id === selectedCountry.id) }"
            @click.stop="selectCode(item)">
            <img class="input-phone__code-item-img" :src="item.flagIcon" alt="">
            <div class="d-flex">
              <span class="mr_8">{{ item.displayName || item.name }}</span>
              <span>{{ item.phoneCode }}</span>
            </div>
          </div>

          <div v-if="!filteredCodes.length" class="input-phone__code-empty body-text color--dark-purple">
            {{ $t('adminTitles.nothingFound') }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import { mapGetters } from 'vuex';
import masker from 'vue-the-mask/src/masker';
import { tokens } from 'vue-the-mask';
import { filterPhoneCodes } from '@/services/phoneCountries';

export default {
  name: 'InputPhone',
  emits: ['valueChange', 'onBlue'],
  props: {
    value: {
      type: String,
      default: '',
    },
    showLabel: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    errorValidation: {
      type: Boolean,
      default: false
    }
  },
  created() {
    document.addEventListener('click', this.onClickOutside);
    this.setInitialValue();
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onClickOutside);
  },
  data() {
    return {
      inputValue: '',
      showCodeSelector: false,
      codeSearch: '',
      selectedCountry: null,
      id: null,
      error: false,
      defaultPhoneMask: '+####################',
    };
  },
  computed: {
    ...mapGetters(['phoneCodes']),
    filteredCodes() {
      return filterPhoneCodes(this.phoneCodes.codes, this.codeSearch);
    },
  },
  methods: {
    toggleCodeSelector() {
      this.showCodeSelector = !this.showCodeSelector;
      this.codeSearch = '';
      if (this.showCodeSelector) {
        this.$nextTick(() => {
          if (this.$refs.codeSearch) this.$refs.codeSearch.focus();
        });
      }
    },
    setInitialValue() {
      if (this.value) {
        const selectedCountry = this.phoneCodes.codes.find((val) => this.value.startsWith(val.phoneCode));
        if (selectedCountry) {
          this.selectedCountry = selectedCountry;
          this.inputValue = this.value.replace(selectedCountry.phoneCode, '');
        } else {
          this.inputValue = this.value;
          this.selectedCountry = null;
        }
      } else {
        this.selectedCountry = this.phoneCodes.codes[0] || {};
      }
    },
    onClickOutside(event) {
      if (this.showCodeSelector && this.$refs.codeElement && !this.$refs.codeElement.contains(event.target)) {
        this.showCodeSelector = false;
      }
    },
    selectCode(code) {
      this.selectedCountry = code;
      this.showCodeSelector = false;
      this.codeSearch = '';
      this.inputValue = '';
      this.$emit('valueChange', this.inputValue);
      this.$refs.input.focus();
    },
    unmask(maskedValue, mask) {
      return masker(maskedValue, mask, false, tokens);
    },
    valueChange() {
      if (this.selectedCountry && this.inputValue) {
        this.$emit('valueChange', `${this.selectedCountry.phoneCode}${this.unmask(this.inputValue, this.selectedCountry.phoneMobileMask)}`);
      } else if (this.inputValue) {
        this.$emit('valueChange', this.unmask(this.inputValue, this.defaultPhoneMask));
      } else {
        this.$emit('valueChange', this.inputValue);
      }
    },
    validate() {
      this.error = this.selectedCountry ? this.checkUnmaskedPhoneLength() : !this.inputValue.length;
      return this.error;
    },
    clear() {
      this.inputValue = '';
    },
    checkUnmaskedPhoneLength() {
      let err = true;
      if (this.selectedCountry && this.selectedCountry.phoneMasks) {
        this.selectedCountry.phoneMasks.forEach((mask) => {
          if (!this.inputValue.length || this.selectedCountry.phoneLengthes.includes(this.unmask(this.inputValue, mask).length)) {
            err = false;
          }
        });
      }
      return err;
    },
    handlePaste(event) {
      const rawText = event.clipboardData.getData('text').trim();
      const digitsOnly = rawText.replace(/\D/g, '');

      // If the pasted digits are already a valid number for the currently
      // selected country, just paste them as-is - don't try to guess a
      // different country code (avoids e.g. a Mexican number starting with
      // "7" being mistaken for Russia's "+7" code).
      const staysValidForCurrent = !!(this.selectedCountry && this.selectedCountry.phoneLengthes
        && this.selectedCountry.phoneLengthes.includes(digitsOnly.length));

      if (!staysValidForCurrent) {
        // Otherwise, look for a country whose code prefixes the pasted digits
        // and whose remainder is itself a valid national number length for it.
        const phoneCode = this.phoneCodes.codes.find((code) => {
          const codeDigits = code.phoneCode.replace('+', '');
          if (!codeDigits || !digitsOnly.startsWith(codeDigits)) return false;
          const rest = digitsOnly.slice(codeDigits.length);
          return code.phoneLengthes && code.phoneLengthes.includes(rest.length);
        });

        if (phoneCode) {
          event.preventDefault();
          this.selectedCountry = phoneCode;
          this.inputValue = digitsOnly.slice(phoneCode.phoneCode.replace('+', '').length);
        }
      }
      this.valueChange();
    }
  },
  watch: {
    // When the parent updates :value after this component has already mounted
    // (e.g. an async autofill from OCR), re-parse the new value into
    // selectedCountry + inputValue. Without this watcher InputPhone
    // silently ignores anything assigned to userForm.phone post-mount.
    value(newVal) {
      const currentEmitted = this.selectedCountry && this.inputValue
        ? `${this.selectedCountry.phoneCode}${this.unmask(this.inputValue, this.selectedCountry.phoneMobileMask)}`
        : this.unmask(this.inputValue || '', this.defaultPhoneMask);
      if (newVal === currentEmitted) return;
      this.setInitialValue();
    },
  },
};
</script>
<style lang="scss" scoped>
.input-phone__wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;

  &--disabled {
    * {
      pointer-events: none;
      color: var(--solid-dark-purple) !important;
    }

    .input-phone {
      border-color: var(--solid-lavender-grey) !important;
      background: #F8F8F8;
    }

    .input-phone__code-arrow {
      opacity: 0.7;
    }

    .input-phone__code {
      span {
        color: #C0C4CC;
      }
    }

    ::-webkit-input-placeholder {
      /* Chrome/Opera/Safari */
      color: #C0C4CC;
    }

    ::-moz-placeholder {
      /* Firefox 19+ */
      color: #C0C4CC;
    }

    :-ms-input-placeholder {
      /* IE 10+ */
      color: #C0C4CC;
    }
  }
}

.input-phone__label {
  margin-bottom: 4px;
}

.input-phone {
  display: flex;
  align-items: center;
  width: 100%;
  height: 40px;
  border: 1px solid var(--solid-lavender-grey);
  border-radius: 8px;
  position: relative;

  &--error {
    border: 1px solid var(--solid-red) !important;
  }
}

.input-phone__code {
  position: relative;
  display: flex;
  align-items: center;
  height: 100%;
  width: 90px;
  gap: 8px;
  padding-left: 16px;
  cursor: pointer;

  &:after {
    content: '';
    position: absolute;
    right: 0px;
    height: 16px;
    width: 1px;
    background-color: var(--solid-lavender-grey);
  }
}

.input-phone__code-btn {
  height: 16px;
  padding-right: 16px;
}

.input-phone__code-arrow {
  transform: rotate(-90deg);
}

.input-phone__code-selector {
  position: absolute;
  overflow: hidden;
  top: 48px;
  left: 0px;
  width: 260px;
  border-radius: 8px;
  border: 1px solid var(--solid-lavender-grey);
  background-color: white;
  z-index: 100;
}

.input-phone__code-search {
  padding: 8px;
  border-bottom: 1px solid var(--solid-lavender-grey);
  background-color: white;
}

.input-phone__code-search-input {
  width: 100%;
  height: 32px;
  padding: 0 12px;
  border: 1px solid var(--solid-lavender-grey);
  border-radius: 8px;
  outline: none;
  background-color: transparent;
}

.input-phone__code-list {
  overflow: auto;
  max-height: 240px;
}

.input-phone__code-empty {
  padding: 12px 16px;
}

.input-phone__code-item {
  min-height: 40px;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  cursor: pointer;
  transition: .3s;
  background-color: white;

  &:hover,
  &--active {
    background-color: var(--solid-lavender-ligth);
  }

  .d-flex {
    min-width: 0;
    flex: 1;
    align-items: center;
    justify-content: space-between;

    span:first-child {
      white-space: normal;
      overflow-wrap: anywhere;
    }

    span:last-child {
      flex-shrink: 0;
    }
  }
}

.input-phone__code-item-img {
  width: 21px;
  height: 16px;
  flex-shrink: 0;
  object-fit: contain;
}

.input-phone__form {
  outline: none;
  border: none;
  background-image: none;
  background-color: transparent;
  -webkit-box-shadow: none;
  -moz-box-shadow: none;
  box-shadow: none;
  width: calc(100% - 90px);
  padding: 12px 16px;
}

::-webkit-input-placeholder {
  /* Chrome/Opera/Safari */
  color: var(--solid-dark-purple);
}

::-moz-placeholder {
  /* Firefox 19+ */
  color: var(--solid-dark-purple);
}

:-ms-input-placeholder {
  /* IE 10+ */
  color: var(--solid-dark-purple);
}

:-moz-placeholder {
  /* Firefox 18- */
  color: var(--solid-dark-purple);
}

.input-phone__label:before {
  content: '*';
  color: #F56C6C;
  margin-right: 2px;
}
.el-form-item__error {
  font-family: "Inter", serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 12px;
  letter-spacing: 0em;
  text-align: left;
  color: var(--solid-red);
  padding-left: 19px;
}

.el-form-item__error:before {
  content: "";
  position: absolute;
  left: 0;
  top: 4px;
  width: 11px;
  height: 11px;
  background-size: contain;
  background-image: url(http://localhost:8080/img/err-notification.bc22c826.svg);
}
</style>
